import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { SiteNav, SiteFooter } from "../components/SiteChrome";
import { SlashDivider } from "../components/SlashLogo";
import { Sequence } from "../components/Sequence";
import { LoadReveal } from "../components/LoadReveal";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  FORMSPREE_ENDPOINT,
  EASE_STAGE,
} from "../lib/constants";

// ── Content ─────────────────────────────────────────────────────────────
const BUILD_STAGES = [
  { name: "Website", hint: "Fast, semantic, indexable", detail: "The front door. Editorial layout, mobile-first, structured data, semantic HTML, and Core Web Vitals tuned for real ranking." },
  { name: "CRM", hint: "GoHighLevel pipelines", detail: "Every lead, deal, and vendor interaction lives in one pipeline with stages, custom properties, and follow-up cadences." },
  { name: "AI", hint: "Drafting, sorting, summarizing", detail: "AI assists communication and repetitive admin. Reply drafts, intent classification, thread summaries. Owner-approved sends, not autopilot." },
  { name: "Automation", hint: "Trigger, condition, action", detail: "Missed-call text-back, estimate follow-up, no-show recovery, review requests. The system runs while the operator is in the field." },
  { name: "SEO", hint: "Semantic + entity-based", detail: "Technical SEO, on-page structure, local business schema, entity-based content architecture built for organic reach." },
  { name: "Analytics", hint: "Signal over vanity", detail: "Pipeline conversion, source attribution, response-time SLA, stale-lead surfacing. Numbers you can act on." },
  { name: "Growth", hint: "Continuous optimization", detail: "The system is not shipped once. Ongoing tuning of copy, sequences, funnels, and workflows against real pipeline data." },
];

const FLOW_STAGES = [
  { name: "Traffic", hint: "Organic, direct, referral", detail: "SEO, local presence, and referral loops feed qualified attention into the system." },
  { name: "Lead Capture", hint: "Form, call, text", detail: "Web forms, missed-call text-back, and instant chat routed to the pipeline in real time." },
  { name: "Qualification", hint: "Sort by intent and fit", detail: "AI-assisted classification plus manual review. Only qualified leads move to booking." },
  { name: "Booking", hint: "Owner or auto", detail: "Same-day booking with confirmation and reminder cadences." },
  { name: "Follow-up", hint: "Multi-step, multi-channel", detail: "SMS and email sequences for estimates, no-shows, and stalled deals." },
  { name: "Retention", hint: "Post-job engagement", detail: "Review requests, referral asks, and re-engagement for lapsed customers." },
  { name: "Reporting", hint: "Weekly, actionable", detail: "One view of pipeline, conversion, response time, and revenue at risk." },
];

const MEASUREMENT = [
  { label: "Pipeline health", title: "Lead-to-booking conversion by source", detail: "Every source is tracked from first touch to booked appointment. We see where leads convert and where they leak." },
  { label: "Response speed", title: "First-response SLA tracking", detail: "Time from lead arrival to first human or automated response. Sub-minute for automation, target windows for human follow-up." },
  { label: "Stale opportunity", title: "Estimates and appointments aging out", detail: "Any estimate or appointment that goes cold gets flagged with a recommended action. Nothing sits silently in the pipeline." },
  { label: "Cadence integrity", title: "Automated follow-up delivery rates", detail: "SMS and email sequence delivery, open, and reply rates. We tune messages against real data, not guesses." },
  { label: "Attribution", title: "Source-to-revenue mapping", detail: "Which channel produced which booked job and which revenue. Marketing spend and organic effort are held to the same standard." },
  { label: "Operational load", title: "Owner-time in the system", detail: "Hours the owner spends inside the CRM per week. The goal is less manual work over time, not more dashboards." },
];

const TECH_STACK = [
  { name: "GoHighLevel", role: "CRM and Automation", detail: "Primary pipeline, follow-up sequences, and workflow automation. The operational spine of every HOL build." },
  { name: "Emergent", role: "AI Product Development", detail: "AI-assisted development platform used to build custom tools and integrations tuned to the operation." },
  { name: "Higgsfield", role: "Video and Visual AI", detail: "Used for select brand and visual work when a build needs it. Not a substitute for real photography of real work." },
];

const INDUSTRIES = [
  { name: "Junk Removal and Cleanouts", detail: "Operators clearing homes, estates, and post-renovation sites. Lead volume is not the problem, follow-up is." },
  { name: "Property Services", detail: "Floor prep, debris hauling, turnover readiness, donation-conscious item recovery for landlords and investors." },
  { name: "Real Estate Support", detail: "Wholesalers, investors, agents, and brokers who need operational systems behind their deal flow." },
  { name: "Home Services", detail: "Local operators in trades, cleaning, and property maintenance who need real intake and follow-up infrastructure." },
  { name: "Field Service Small Teams", detail: "Businesses running crews and appointments. The operational load scales faster than the crew does." },
  { name: "Small Business Operators", detail: "Owner-operators building past the founder-does-everything phase. The system is the next hire." },
];

const AGENCY_STAGES = ["Build", "Leave"];
const HOL_STAGES = ["Build", "Optimize", "Automate", "Measure", "Improve"];

const FAQ = [
  {
    q: "How is HOL different from a marketing agency?",
    a: "Marketing agencies focus on the top of the funnel. Ads, brand, traffic. HOL builds the operational layer that runs after the lead arrives. Follow-up. Scheduling. Pipeline. Retention. The layer most agencies do not touch is the layer that decides whether traffic turns into revenue.",
  },
  {
    q: "Do you require a long-term contract?",
    a: "Engagements are scoped to the build. Once the operational system is running, ongoing optimization is a monthly retainer that can be paused or ended without penalty. If the system is not delivering value, you should not be locked in.",
  },
  {
    q: "Which CRM do you build in?",
    a: "GoHighLevel is the primary platform. It handles CRM, automation, SMS, email, calendars, and reporting under one roof. For teams already on another platform, we scope compatibility during the audit.",
  },
  {
    q: "What is a Growth Strategy Session?",
    a: "A working session where we map your current lead flow, response times, CRM setup, follow-up cadences, and revenue leaks. It ends with a written build plan and prioritized next steps. You can act on it whether you engage HOL or not.",
  },
  {
    q: "Do you write copy and content, or do we?",
    a: "Both. Initial system copy, sequence messages, and landing page content are drafted by HOL. Ongoing edits, offers, and business-specific voice come from the operator. We produce, you own.",
  },
  {
    q: "How do you handle privacy and data?",
    a: "Client data lives in the client's own CRM instance. HOL accesses it under a scoped agreement to build and tune the system. No client data is used to train external models.",
  },
];

// ── Reveal helper ────────────────────────────────────────────────────────
function Reveal({ children, delay = 0, y = 20 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.7, ease: EASE_STAGE, delay }}
    >
      {children}
    </motion.div>
  );
}

// ── Sections ─────────────────────────────────────────────────────────────
function Hero() {
  const scrollTo = (hash) => (e) => {
    e.preventDefault();
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className="hero" data-testid="hero-section">
      <div className="wrap">
        <div className="hero-inner">
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 1.9, duration: 0.9, ease: EASE_STAGE }}
            style={{ marginBottom: 40 }}
          >
            <img
              src="/hol/logo-transparent.png"
              alt="HOL Creative Solutions LLC"
              width={260}
              height={260}
              data-testid="hero-logo-image"
              style={{ display: "block", margin: "0 auto", maxWidth: "min(260px, 60vw)", height: "auto", objectFit: "contain" }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.05, duration: 0.6, ease: EASE_STAGE }}
          >
            <span className="eyebrow" data-testid="hero-eyebrow">Operations Infrastructure for Local Service Businesses</span>
          </motion.div>

          <motion.h1
            className="hero-headline"
            data-testid="hero-heading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.15, duration: 0.8, ease: EASE_STAGE }}
          >
            The operating system for service businesses.
            <br />
            <span className="italic-gold">Built once. Tuned continuously.</span>
          </motion.h1>

          <motion.p
            className="lead hero-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.35, duration: 0.7, ease: EASE_STAGE }}
          >
            Websites, CRM, AI, automation, SEO, and analytics assembled into one operational layer.
            <br />
            Not a stack of tools. A system that runs.
          </motion.p>

          <motion.div
            className="hero-ctas"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.5, duration: 0.6, ease: EASE_STAGE }}
          >
            <a href="#contact" onClick={scrollTo("#contact")} className="btn btn-primary" data-testid="hero-cta-audit">
              Book a Growth Strategy Session
            </a>
            <a href="#build" onClick={scrollTo("#build")} className="btn btn-ghost" data-testid="hero-cta-build">
              See What We Build
            </a>
          </motion.div>

          <motion.div
            className="hero-meta"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.7, duration: 0.6 }}
          >
            <span>Central Indiana and beyond</span>
            <span>Local service, field service, home service</span>
            <span>Ongoing, not one-shot</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function WhatWeBuild() {
  return (
    <section id="build" data-testid="build-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">01</span>
            <span className="eyebrow">What We Build</span>
          </div>
          <Reveal><h2>An ecosystem, not a website.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="lead">
              Seven connected layers assembled into one operational system.
              <br />
              Each stage feeds the next. Each connection is measurable.
            </p>
          </Reveal>
        </div>
        <Sequence items={BUILD_STAGES} variant="build" />
      </div>
      <SlashDivider />
    </section>
  );
}

function HowWeWork() {
  return (
    <section id="how" data-testid="how-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">02</span>
            <span className="eyebrow">How We Work</span>
          </div>
          <Reveal><h2>The flow a lead moves through.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="lead">
              From first click to reporting.
              <br />
              Every stage is a place a business can lose revenue if the system is not tuned.
            </p>
          </Reveal>
        </div>
        <Sequence items={FLOW_STAGES} variant="flow" />
      </div>
      <SlashDivider />
    </section>
  );
}

function Measurement() {
  return (
    <section id="measure" data-testid="measure-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">03</span>
            <span className="eyebrow">How We Measure Success</span>
          </div>
          <Reveal><h2>Methodology first. Numbers are the client&rsquo;s, not ours.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="lead">
              We do not publish before-and-after metrics without permission. Here is what gets tracked and how we act on it.
            </p>
          </Reveal>
        </div>
        <div className="methodology" data-testid="methodology-grid">
          {MEASUREMENT.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.06}>
              <div className="methodology-card" data-testid={`methodology-card-${i}`}>
                <span className="meta-label">{m.label}</span>
                <h3>{m.title}</h3>
                <p>{m.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <SlashDivider />
    </section>
  );
}

function TechStack() {
  return (
    <section id="tech" data-testid="tech-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">04</span>
            <span className="eyebrow">Powered By</span>
          </div>
          <Reveal><h2>Real tools. In production. Today.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="lead">
              We list what we build in, not what sounds good.
              <br />
              Additional tools are added when a specific engagement requires them.
            </p>
          </Reveal>
        </div>
        <div className="tech-row" data-testid="tech-row">
          {TECH_STACK.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className="tech-tile" data-testid={`tech-tile-${i}`}>
                <span className="tech-role">{t.role}</span>
                <div className="tech-name">{t.name}</div>
                <p>{t.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <SlashDivider />
    </section>
  );
}

function Industries() {
  return (
    <section id="industries" data-testid="industries-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">05</span>
            <span className="eyebrow">Industries We Serve</span>
          </div>
          <Reveal><h2>Local service. Field service. Home service.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="lead">
              Businesses where the operational layer is the constraint, not the demand.
            </p>
          </Reveal>
        </div>
        <div className="industries" data-testid="industries-grid">
          {INDUSTRIES.map((it, i) => (
            <Reveal key={it.name} delay={i * 0.06}>
              <div className="industry-tile" data-testid={`industry-tile-${i}`}>
                <div className="industry-name">{it.name}</div>
                <p>{it.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <SlashDivider />
    </section>
  );
}

function WhyHOL() {
  return (
    <section id="why" data-testid="why-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">06</span>
            <span className="eyebrow">Why HOL</span>
          </div>
          <Reveal><h2>Two sequences. One keeps going.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="lead">
              Most agencies stop the day the build ships.
              <br />
              HOL keeps the system tuned against real pipeline data.
            </p>
          </Reveal>
        </div>

        <div className="dual-sequence" data-testid="dual-sequence">
          <div>
            <div className="dual-col-label">Typical agency</div>
            <div className="dual-track">
              {AGENCY_STAGES.map((s, i) => (
                <motion.div
                  key={s}
                  className="dual-node dim"
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5, ease: EASE_STAGE, delay: i * 0.14 }}
                >
                  {s}
                </motion.div>
              ))}
              <motion.div
                className="dual-terminator"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.4, delay: AGENCY_STAGES.length * 0.14 + 0.1 }}
              >
                Sequence ends
              </motion.div>
            </div>
          </div>
          <div>
            <div className="dual-col-label hol">HOL Creative Solutions</div>
            <div className="dual-track">
              {HOL_STAGES.map((s, i) => (
                <motion.div
                  key={s}
                  className="dual-node hol"
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.55, ease: EASE_STAGE, delay: i * 0.16 }}
                >
                  {s}
                </motion.div>
              ))}
              <motion.div
                className="dual-terminator"
                style={{ color: "var(--gold)" }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.4, delay: HOL_STAGES.length * 0.16 + 0.1 }}
              >
                sequence continues
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <SlashDivider />
    </section>
  );
}

function FAQBlock() {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section id="faq" data-testid="faq-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">07</span>
            <span className="eyebrow">FAQ</span>
          </div>
          <Reveal><h2>Practical questions. Practical answers.</h2></Reveal>
        </div>
        <div className="faq" data-testid="faq-list">
          {FAQ.map((item, i) => {
            const open = openIndex === i;
            return (
              <div key={item.q} className={`faq-item ${open ? "open" : ""}`} data-testid={`faq-item-${i}`}>
                <button
                  className="faq-q"
                  onClick={() => setOpenIndex(open ? null : i)}
                  aria-expanded={open}
                  aria-controls={`faq-a-${i}`}
                  data-testid={`faq-q-${i}`}
                >
                  <span>{item.q}</span>
                  <span className="faq-toggle" aria-hidden="true">+</span>
                </button>
                <div id={`faq-a-${i}`} className="faq-a" role="region">
                  <div className="faq-a-inner">{item.a}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <SlashDivider />
    </section>
  );
}

const HELP_OPTIONS = [
  "CRM setup",
  "Lead follow-up automation",
  "Website and funnel support",
  "AI workflow support",
  "Operations support",
  "Not sure yet",
];
const INITIAL_FORM = { name: "", business: "", email: "", phone: "", help: "", details: "" };

function ContactBlock() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [gotcha, setGotcha] = useState(""); // honeypot; real users never touch this
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus({ state: "error", message: "Name and email are required." });
      return;
    }
    // Honeypot: any value here means it's a bot. Fail silently so the bot
    // sees a "success" state and moves on without alerting Formspree.
    if (gotcha) {
      setStatus({ state: "success", message: "Thanks. We will reach out shortly to schedule your session." });
      setForm(INITIAL_FORM);
      return;
    }
    setStatus({ state: "submitting", message: "Sending..." });
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...form, _gotcha: gotcha, _subject: `Growth Strategy Session Request from ${form.name}` }),
      });
      if (res.ok) {
        setStatus({ state: "success", message: "Thanks. We will reach out shortly to schedule your session." });
        setForm(INITIAL_FORM);
      } else {
        setStatus({ state: "error", message: `Something went wrong. Email us directly at ${CONTACT_EMAIL}.` });
      }
    } catch {
      setStatus({ state: "error", message: `Network error. Email us directly at ${CONTACT_EMAIL}.` });
    }
  };

  return (
    <section id="contact" data-testid="contact-section">
      <div className="wrap">
        <div className="section-header">
          <div className="eyebrow-row">
            <span className="section-index">08</span>
            <span className="eyebrow">Book a Growth Strategy Session</span>
          </div>
          <Reveal><h2>Tell us about the operation. We come back with a plan.</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="lead">
              Typical reply time is the same business day.
              <br />
              You get a written build plan you can act on, whether you engage HOL or not.
            </p>
          </Reveal>
        </div>

        <div className="contact-block" data-testid="contact-block">
          <form className="contact-form" onSubmit={onSubmit} data-testid="contact-form" noValidate>
            {/* Honeypot: hidden from users and assistive tech, spammers fill it. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label htmlFor="cf-gotcha">Leave this field empty</label>
              <input
                id="cf-gotcha"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={gotcha}
                onChange={(e) => setGotcha(e.target.value)}
                data-testid="contact-input-gotcha"
              />
            </div>
            <div className="contact-form-row">
              <label htmlFor="cf-name">Name</label>
              <input id="cf-name" type="text" value={form.name} onChange={onChange("name")} required data-testid="contact-input-name" />
            </div>
            <div className="contact-form-row">
              <label htmlFor="cf-business">Business Name</label>
              <input id="cf-business" type="text" value={form.business} onChange={onChange("business")} data-testid="contact-input-business" />
            </div>
            <div className="contact-form-grid-2">
              <div className="contact-form-row">
                <label htmlFor="cf-email">Email</label>
                <input id="cf-email" type="email" value={form.email} onChange={onChange("email")} required data-testid="contact-input-email" />
              </div>
              <div className="contact-form-row">
                <label htmlFor="cf-phone">Phone</label>
                <input id="cf-phone" type="tel" value={form.phone} onChange={onChange("phone")} data-testid="contact-input-phone" />
              </div>
            </div>
            <div className="contact-form-row">
              <label htmlFor="cf-help">What kind of help do you need?</label>
              <select id="cf-help" value={form.help} onChange={onChange("help")} data-testid="contact-input-help">
                <option value="">Select an option</option>
                {HELP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="contact-form-row">
              <label htmlFor="cf-details">A few details (optional)</label>
              <textarea id="cf-details" rows="4" value={form.details} onChange={onChange("details")} data-testid="contact-input-details" />
            </div>
            {status.state !== "idle" && (
              <div className={`contact-form-status status-${status.state}`} role="status" data-testid="contact-form-status">
                {status.message}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={status.state === "submitting"} data-testid="contact-form-submit">
              {status.state === "submitting" ? "Sending..." : "Request a Growth Strategy Session"}
            </button>
          </form>

          <aside className="contact-side" data-testid="contact-side">
            <h3>Or reach us directly.</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              <a href={`tel:${CONTACT_PHONE_TEL}`} data-testid="contact-side-phone" style={{ fontWeight: 600, fontSize: "1.05rem" }}>
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-side-email" style={{ color: "var(--text-muted)" }}>
                {CONTACT_EMAIL}
              </a>
            </div>
            <p className="contact-side-meta">
              If your operation is bleeding leads right now, call directly.
              <br />
              If the situation is more strategic, use the form.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

// ── Structured data (LocalBusiness + FAQ) ─────────────────────────────────
function StructuredData() {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

// ── Page ─────────────────────────────────────────────────────────────────
export default function Home() {
  useEffect(() => {
    document.title = "HOL Creative Solutions LLC | Operations Infrastructure for Local Service Businesses";
    // Scroll to hash after load
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  return (
    <div data-testid="home-page">
      <a href="#build" className="skip-link">Skip to content</a>
      <LoadReveal />
      <SiteNav />
      <main>
        <Hero />
        <WhatWeBuild />
        <HowWeWork />
        <Measurement />
        <TechStack />
        <Industries />
        <WhyHOL />
        <FAQBlock />
        <ContactBlock />
      </main>
      <SiteFooter />
      <StructuredData />
    </div>
  );
}
